import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useI18n } from '../i18n';
import { en, km } from './CtaBanner.data';

interface CtaBannerProps {
  title: string;
  description: string;
  primaryLabel?: string;
  primaryTo?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
  secondaryTo?: string;
}

const CtaBanner: React.FC<CtaBannerProps> = ({
  title,
  description,
  primaryLabel,
  primaryTo,
  secondaryLabel,
  secondaryHref,
  secondaryTo,
}) => {
  const { locale, localePath } = useI18n();
  const copy = locale === 'km' ? km : en;

  return (
    <Banner>
      <div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <Actions>
        <PrimaryButton to={localePath(primaryTo ?? copy.defaultPrimaryTo)}>
          {primaryLabel ?? copy.defaultPrimaryLabel}
        </PrimaryButton>
        {secondaryHref && (
          <SecondaryAnchor href={secondaryHref} target="_blank" rel="noopener noreferrer">
            {secondaryLabel}
          </SecondaryAnchor>
        )}
        {secondaryTo && <SecondaryLink to={localePath(secondaryTo)}>{secondaryLabel}</SecondaryLink>}
      </Actions>
    </Banner>
  );
};

const Banner = styled.section`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 2.5rem 2.75rem;
  margin-top: 2.75rem;
  border-radius: 24px;
  background:
    radial-gradient(600px 260px at 92% -20%, rgba(255, 255, 255, 0.22) 0%, transparent 60%),
    linear-gradient(135deg, #0a5f8e 0%, #008ecc 52%, #1fb1e6 100%);
  color: white;
  box-shadow: 0 24px 48px rgba(0, 110, 159, 0.25);

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.14);
    pointer-events: none;
  }

  &::before {
    width: 22rem;
    height: 22rem;
    right: -6rem;
    top: -12rem;
  }

  &::after {
    width: 14rem;
    height: 14rem;
    right: 6rem;
    bottom: -9rem;
  }

  > div {
    position: relative;
    z-index: 1;
  }

  h2 {
    font-size: clamp(1.4rem, 2.4vw, 1.8rem);
    letter-spacing: -0.02em;
    margin-bottom: 0.55rem;
    color: white;
  }

  p {
    margin: 0;
    max-width: 42rem;
    line-height: 1.65;
    color: rgba(255, 255, 255, 0.92);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 1.9rem;
    border-radius: 20px;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  flex-shrink: 0;
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.35rem;
  background: white;
  color: #0b6fa3;
  font-weight: 700;
  border-radius: 999px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
  }
`;

const secondaryStyles = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.35rem;
  border: 1.5px solid rgba(255, 255, 255, 0.7);
  color: white;
  font-weight: 650;
  border-radius: 999px;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const SecondaryLink = styled(Link)`
  ${secondaryStyles}
`;

const SecondaryAnchor = styled.a`
  ${secondaryStyles}
`;

export default CtaBanner;
